import { useId, type ComponentPropsWithRef } from "react";
import { cx } from "../../cx";

export interface CheckboxProps extends Omit<ComponentPropsWithRef<"input">, "type" | "size"> {
  /** Visible label (required for accessibility). */
  readonly label: string;
  /** Secondary text under the label, linked with `aria-describedby`. */
  readonly hint?: string;
  /** Keep the label for assistive tech only (e.g. a per-row selector in a table). */
  readonly hideLabel?: boolean;
  readonly containerClassName?: string;
}

/**
 * Native checkbox with its label at the inline end of the box (so the box leads in both
 * directions). Uses the browser control tinted with the primary action token.
 */
export function Checkbox({
  label,
  hint,
  hideLabel = false,
  id,
  className,
  containerClassName,
  disabled,
  ...rest
}: CheckboxProps) {
  const autoId = useId();
  const inputId = id ?? `checkbox-${autoId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  return (
    <div className={cx("flex items-start gap-3", containerClassName)}>
      <input
        id={inputId}
        type="checkbox"
        disabled={disabled}
        aria-describedby={hintId}
        className={cx(
          "mt-0.5 size-4 shrink-0 cursor-pointer rounded-sm accent-action-primary",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
          "disabled:cursor-not-allowed disabled:opacity-70",
          className,
        )}
        {...rest}
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <label
          htmlFor={inputId}
          className={cx(
            "text-sm font-medium text-fg",
            disabled ? "opacity-70" : "cursor-pointer",
            hideLabel && "sr-only",
          )}
        >
          {label}
        </label>
        {hint && (
          <p id={hintId} className="text-xs text-fg-muted">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}
